
// NOTE: This module should have no dependencies!

export interface IEventRecord {
    target: any;
    eventName: string;
    parent: any;
    callback: (args?: any) => void;
    elementCallback: (...args: any[]) => void;
    objectCallback: (args?: any) => void;
    useCapture: boolean;
}

export interface IEventRecordsByName {
    [eventName: string]: IEventRecordList;
}

export type IEventRecordList = {
    [id: string]: IEventRecord[];
} & {
        count: number;
    };

export interface IEventHost {
    __declaredEvents?: IDeclaredEventsByName;
}

export interface IDeclaredEventsByName {
    [eventName: string]: boolean;
}

export interface IEventTarget {
    parent: IEventTarget;
    __events__: IEventRecordsByName;
}

export interface ICustomEvent<T> extends Event {
    args?: T;
}

export function isDOMEventTarget(this: void, target: {}): target is EventTarget {
    return !!target && typeof (<EventTarget>target).addEventListener === 'function';
}

export function isEventTarget(this: void, target: {}): target is IEventTarget {
    return !!target && !!(<IEventTarget>target).__events__;
}

export function declare(this: void, parent: {}, event: string | string[]) {
    const host: IEventHost = parent;

    const declaredEvents = host.__declaredEvents = host.__declaredEvents || {};

    if (typeof event === 'string') {
        declaredEvents[event] = true;
    } else {
        for (let i = 0; i < event.length; i++) {
            declaredEvents[event[i]] = true;
        }
    }
}

export function raise<T = {}>(
    this: void,
    target: {},
    eventName: string,
    eventArgs?: T,
    bubbleEvent?: boolean
): boolean | undefined {
    let retVal: boolean | undefined;

    if (isDOMEventTarget(target)) {
        if (document.createEvent) {
            const ev: ICustomEvent<T> = document.createEvent('HTMLEvents');

            ev.initEvent(eventName, bubbleEvent, true);
            ev.args = eventArgs;
            retVal = target.dispatchEvent(ev);
        }
    } else {
        while (target && retVal !== false) {
            if (isEventTarget(target)) {
                const events = target.__events__;
                const eventRecords = events[eventName];

                for (let id in eventRecords) {
                    const eventRecordList = eventRecords[id];

                    for (let listIndex = 0; retVal !== false && listIndex < eventRecordList.length; listIndex++) {
                        const record = eventRecordList[listIndex];

                        if (record.objectCallback) {
                            retVal = record.objectCallback.call(record.parent, eventArgs);
                        }
                    }
                }

                // If the target has a parent, bubble the event up.
                target = bubbleEvent ? target.parent : null;
            } else {
                target = null;
            }
        }
    }

    return retVal;
}
